import React, { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { Cull, CullQuery } from '../types';
import { cullsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import './DataTable.css';

const columnHelper = createColumnHelper<Cull>();

const DataTable: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<Cull[]>([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this record? This action cannot be undone.')) {
      try {
        await cullsApi.deleteCull(id);
        // Refresh the data
        if (user?.uid) {
          const culls = await cullsApi.getUserCulls(user.uid, { limit: 1000 });
          setData(culls);
        }
      } catch (error: any) {
        console.error('Error deleting cull:', error);
        alert(`Error deleting record: ${error.message || error}`);
      }
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('date', {
        header: 'Date',
        cell: (info) => format(new Date(info.getValue()), 'dd/MM/yyyy'),
      }),
      columnHelper.accessor('time', {
        header: 'Time',
      }),
      columnHelper.accessor('location_name', {
        header: 'Location',
      }),
      columnHelper.accessor('species', {
        header: 'Species',
      }),
      columnHelper.accessor('sex', {
        header: 'Sex',
      }),
      columnHelper.accessor('age_estimate', {
        header: 'Age',
      }),
      columnHelper.accessor('weight', {
        header: 'Weight (kg)',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('condition', {
        header: 'Condition',
      }),
      columnHelper.accessor('antlers_present', {
        header: 'Antlers',
        cell: (info) => info.getValue() ? 'Yes' : 'No',
      }),
      columnHelper.accessor('shot_placement', {
        header: 'Shot Placement',
      }),
      columnHelper.accessor('shot_distance', {
        header: 'Distance',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <div className="table-actions">
            <Link
              to={`/view-cull/${info.row.original.id}`}
              className="action-btn view-btn"
              title="View record"
            >
              👁️
            </Link>
            <Link
              to={`/edit-cull/${info.row.original.id}`}
              className="action-btn edit-btn"
              title="Edit record"
            >
              ✏️
            </Link>
            <button
              onClick={() => handleDelete(info.row.original.id)}
              className="action-btn delete-btn"
              title="Delete record"
            >
              🗑️
            </button>
          </div>
        ),
      }),
    ],
    [handleDelete]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const culls = await cullsApi.getUserCulls(user.uid, { limit: 1000 });
        setData(culls);
      } catch (error: any) {
        console.error('Error fetching culls:', error);
        // You might want to show an error message to the user here
        alert(`Error loading data: ${error.message || error}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const exportToCsv = async () => {
    try {
      const blob = await cullsApi.exportCsv(data);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'culls.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  if (loading) {
    return <div className="loading">Loading data...</div>;
  }

  return (
    <div className="data-table-page">
      <div className="data-table-header">
        <h1>Cull Data</h1>
        <div className="table-controls">
          <input
            value={globalFilter ?? ''}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="search-input"
            placeholder="Search all columns..."
          />
          <button onClick={exportToCsv} className="export-btn">
            Export CSV
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="table-header">
                    {header.isPlaceholder ? null : (
                      <div
                        className={`header-content ${
                          header.column.getCanSort() ? 'cursor-pointer' : ''
                        }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' 🔼',
                          desc: ' 🔽',
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="table-row">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="table-cell">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          {'<<'}
        </button>
        <button
          className="pagination-btn"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          {'<'}
        </button>
        <button
          className="pagination-btn"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          {'>'}
        </button>
        <button
          className="pagination-btn"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
        >
          {'>>'}
        </button>
        <span className="pagination-info">
          Page{' '}
          <strong>
            {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </strong>
        </span>
        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value));
          }}
          className="page-size-select"
        >
          {[10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default DataTable;